import React, { useEffect, useState } from "react";
import { CiLocationOn } from "react-icons/ci";
import { FiDollarSign } from "react-icons/fi";
import { HiDotsHorizontal } from "react-icons/hi";
import { IoCarSportSharp } from "react-icons/io5";
import { useSelector } from "react-redux";
import { Area, AreaChart } from "recharts";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import VehicleDetails from "./VehicleDetails.Dashboard";
import { RentalType } from "../../types";

const activityData = [
  {
    title: "Arrived Customer",
    dsec: "#348 has arrived at the customer",
    time: "5 min ago",
    color: "#5151F9",
    bgColor: "#DCDCFE",
    icon: CiLocationOn,
  },
  {
    title: "Arrived New Car",
    dsec: "Car #27 has arrived at the customer",
    time: "9 min ago",
    color: "#0EA5E9",
    bgColor: "#CFEDFB",
    icon: IoCarSportSharp,
  },
  {
    title: "Arrived Passenger",
    dsec: "Customer #16 has arrived",
    time: "15 min ago",
    color: "#11B981",
    bgColor: "#CFF1E6",
    icon: FiDollarSign,
  },
];

const Analytics = () => {
  const type: "lessee" | "lessor" = useSelector(
    (state: any) => state.dashboardState.dashboardType
  );

  const lesseeRentals: RentalType[] = useSelector(
    (state: any) => state.dashboardState.lesseeRentals
  );

  const lessorRentals: RentalType[] = useSelector(
    (state: any) => state.dashboardState.lessorRentals
  );

  const [lesseeGraphData, setLesseeGraphData] = useState<
    {
      month: string;
      rentals: number;
    }[]
  >();

  const [lessorGraphData, setLessorGraphData] = useState<
    {
      month: string;
      income: number;
    }[]
  >();

  useEffect(() => {
    if (type === "lessee" && lesseeRentals.length) {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const lastSixMonthsRentals = lesseeRentals.filter((rental) => {
        const rentalDate = new Date(rental.rentalStartDatetime);
        return rentalDate >= sixMonthsAgo;
      });

      const rentalsByMonth = lastSixMonthsRentals.map((rental) => {
        const rentalDate = new Date(rental.rentalStartDatetime);
        const monthNames = [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUNE",
          "JULY",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];
        return `${monthNames[rentalDate.getMonth()]}`; // Months are 0-indexed
      });

      const rentalsPerMonth: Record<string, number> = {};
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthNames = [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUNE",
          "JULY",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];
        rentalsPerMonth[`${monthNames[date.getMonth()]}`] = 0;
      }

      // Count the number of rentals per month
      rentalsByMonth.forEach((month) => {
        rentalsPerMonth[month] = (rentalsPerMonth[month] || 0) + 1;
      });

      // Convert the counts object to an array of objects
      const rentalsPerMonthArray = Object.entries(rentalsPerMonth).map(
        ([month, rentals]) => ({
          month,
          rentals,
        })
      );

      // const rentalsPerMonth = rentalsByMonth.reduce((counts, month) => {
      //   counts[month] = (counts[month] || 0) + 1;
      //   return counts;
      // }, {} as Record<string, number>);

      // const graphData = Object.entries(rentalsPerMonth).map(
      //   ([month, count]) => ({
      //     month: month,
      //     rentals: count,
      //   })
      // );

      setLesseeGraphData(rentalsPerMonthArray);
    }

    if (type === "lessor" && lessorRentals.length) {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const lastSixMonthsRentals = lessorRentals.filter((rental) => {
        const rentalDate = new Date(rental.rentalEndDatetime);
        return rentalDate >= sixMonthsAgo;
      });

      const rentalsByMonth = lastSixMonthsRentals.map((rental) => {
        const rentalDate = new Date(rental.rentalEndDatetime);
        const monthNames = [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUNE",
          "JULY",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];
        return {
          month: `${monthNames[rentalDate.getMonth()]}`, // Months are 0-indexed
          depositAmount: parseFloat(rental?.escrows?.depositAmount),
        };
      });

      const incomePerMonth: Record<string, number> = {};
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthNames = [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUNE",
          "JULY",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];
        incomePerMonth[`${monthNames[date.getMonth()]}`] = 0;
      }

      rentalsByMonth.forEach((rental) => {
        incomePerMonth[rental.month] += rental?.depositAmount;
      });

      // Convert the counts object to an array of objects
      const incomePerMonthArray = Object.entries(incomePerMonth).map(
        ([month, income]) => ({
          month,
          income,
        })
      );

      setLessorGraphData(incomePerMonthArray);
    }
  }, [lesseeRentals, type, lessorRentals]);

  return (
    <div className="w-full flex flex-col lg:flex-row gap-10  max-h-fit">
      <div className="flex gap-4 flex-col w-full h-full">
        {type === "lessor" && <VehicleDetails />}

        <div className="w-full bg-white  flex flex-shrink-0 overflow-x-scroll no-scrollbar h-full">
          <div className="flex flex-shrink-0 p-5 overflow-x-scroll h-[315px] no-scrollbar w-full bg-white">
            <ResponsiveContainer width="100%" height="100%">
              {type === "lessee" ? (
                <ComposedChart
                  // width={800}
                  // height={600}
                  data={lesseeGraphData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <defs>
                    <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#66C87B" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  {/* <CartesianGrid strokeDasharray="3" /> */}
                  <XAxis dataKey="month" />
                  <YAxis dataKey="rentals" stroke="#215759" />
                  <Tooltip />
                  {/* <Legend /> */}
                  {/* <Line
                    type="monotone"
                    dataKey="rentals"
                    stroke="#215759"
                    activeDot={{ r: 8 }}
                  /> */}
                  <Area
                    type="monotone"
                    dataKey="rentals"
                    stroke="#66C87B"
                    fill="rgba(25, 179, 117, 0.2)"
                    activeDot={{ r: 8 }}
                  />
                </ComposedChart>
              ) : (
                <AreaChart
                  // width={730}
                  // height={250}
                  data={lessorGraphData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                  // margin={{
                  //   top: 20, right: 20, bottom: 20, left: 20,
                  // }}
                >
                  <XAxis dataKey="month" stroke="#18428C" />
                  <YAxis
                    dataKey="income"
                    tickFormatter={(value) => `$${value}`}
                    axisLine={false}
                    stroke="#18428C"
                  />
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <Area dataKey="income" stroke="#18428C" fill="#18428C" />
                  <Tooltip />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* <div className="bg-white hidden  border rounded-md p-5 md:flex flex-col gap-3 flex-1 w-full lg:w-[35%]">
        <div className="flex justify-between w-full items-center">
          <p className="text-[#3D475C] font-Inter font-medium leading-4">
            Recent Activity
          </p>
          <HiDotsHorizontal className="text-[20px] text-[#9499A1] " />
        </div>
        <div className="flex w-full flex-row lg:flex-col gap-3 overflow-scroll hide-scrollbar hide-scrollbar::-webkit-scrollbar">
          {activityData.map((item, index) => (
            <div
              key={index}
              className={`flex shrink-0 min-w-16 gap-3 w-fit pb-4 mb-4 lg:border-b border-dashed border-[#9499A1]  ${
                index === activityData.length - 1 && "border-none mb-0"
              }`}
            >
              <div
                style={{
                  backgroundColor: item.bgColor,
                }}
                className="flex justify-center items-center rounded-full w-[40px] h-[40px] "
              >
                <item.icon className="text-[15px]" color={item.color} />
              </div>
              <div className="flex flex-1 flex-col gap-2">
                <p className="text-[#3D475C] font-Inter text-sm lg:text-base tracking-[0.509px] font-normal ">
                  {item.title}
                </p>
                <p className="text-[#9499A1] font-Inter text-xs lg:text-sm  font-normal leading-4 tracking-tighter">
                  {item.dsec}
                </p>
                <p
                  style={{
                    backgroundColor: item.bgColor,
                    color: item.color,
                  }}
                  className="font-Inter min-w-full px-3 py-1 text-xs lg:text-sm border rounded-[9px] font-normal leading-4 tracking-tighter"
                >
                  {item.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};

export default Analytics;
